import { getCurrentAdminRbacPermissionsOptions } from '@gozoki/api'
import type { CurrentAdminRbacPermissionsResponse } from '@gozoki/api-types'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { parseRbacContract, resolveRbacSession } from '~/utils/rbac-session'

/** Cadence de la nouvelle tentative tant qu'aucun contrat exploitable n'est arrivé. */
export const RBAC_RETRY_INTERVAL_MS = 15_000

/** Dernier contrat exploitable de la session, avec le compte auquel il appartient. */
interface RememberedContract {
  adminId: number
  contract: CurrentAdminRbacPermissionsResponse
}

/**
 * Cycle de vie des autorisations de l'admin connecté (`GET /rbac/me/permissions`) : requête et
 * nouvel essai, mémorisation du dernier contrat exploitable, état de la session
 * (`resolveRbacSession`) et rechargement des routes quand le contrat revient.
 *
 * Tout est rangé sous l'identifiant de l'admin : le contrat d'un compte n'est jamais servi à un
 * autre connecté après lui sur le même onglet, même quand la requête du second échoue.
 */
export const useRbacSession = (adminId: number | undefined) => {
  const query = useQuery({
    ...getCurrentAdminRbacPermissionsOptions(adminId),
    enabled: adminId !== undefined,
    // Sans contrat exploitable, l'écran « Autorisations indisponibles » bloque le BO, et la
    // requête retente toute seule jusqu'à en obtenir un (502 passager, BO déployé avant le
    // backend).
    retry: 3,
    refetchInterval: (current) =>
      parseRbacContract(current.state.data) === null ? RBAC_RETRY_INTERVAL_MS : false,
  })
  const [remembered, setRemembered] = useState<RememberedContract | null>(null)
  const rememberedContract =
    remembered !== null && remembered.adminId === adminId ? remembered.contract : null

  // Parsé à part : `parseCurrentAdminRbacPermissionsResponse` rend la réponse elle-même (ou une
  // copie quand il écarte un grant hors catalogue), et seule une nouvelle donnée doit changer
  // l'identité du contrat. Sans quoi la mémorisation ci-dessous se relancerait sans fin.
  const currentContract = useMemo(() => parseRbacContract(query.data), [query.data])
  const session = useMemo(
    () =>
      resolveRbacSession({
        hasUser: adminId !== undefined,
        pending: query.isPending,
        error: query.isError,
        contract: currentContract,
        remembered: rememberedContract,
      }),
    [adminId, query.isPending, query.isError, currentContract, rememberedContract],
  )

  // Un compte dont une réponse exploitable est arrivée garde ce contrat pour la session : si la
  // requête suivante échoue, son ability reste en place au lieu de tout refermer.
  useEffect(() => {
    if (session.state === 'ready' && session.contract !== null && adminId !== undefined) {
      setRemembered({ adminId, contract: session.contract })
    }
  }, [session, adminId])

  // Changement de compte : le contrat mémorisé d'un autre admin est oublié. Le cache, lui, disparaît
  // avec la page, rechargée à tout changement d'identité (`utils/session-navigation.ts`).
  useEffect(() => {
    if (remembered !== null && remembered.adminId !== adminId) setRemembered(null)
  }, [remembered, adminId])

  // L'ability des routes (`beforeLoad` de `/_authenticated`) a été construite sans contrat pendant
  // l'indisponibilité : une fois le contrat arrivé, on recharge les routes pour la reconstruire,
  // sans quoi la page courante resterait sur « Accès refusé ».
  const router = useRouter()
  const wasUnavailable = useRef(false)
  useEffect(() => {
    if (session.state === 'unavailable') {
      wasUnavailable.current = true
    } else if (session.state === 'ready' && wasUnavailable.current) {
      wasUnavailable.current = false
      void router.invalidate()
    }
  }, [session.state, router])

  return { query, session }
}
